import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient, ApolloError } from '@apollo/client';
import { message, Modal, notification } from 'antd';
import { flow, set, get } from 'lodash/fp';
import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as api from '../../../../api';
import { useAccount } from '../../../../components/contexts/AccountContextManager';
import { type SimpleListItemProps } from '../../../../components/SimpleListItem';
import { base64ToArrayBuffer, arrayBufferToBase64 } from '../../../../utilities/buffer';

const convertResponse = (credential: Credential) => {
    const convertBufferPathToString = (path: string) => set(path, arrayBufferToBase64(get(path, credential)));

    return flow([
        convertBufferPathToString('rawId'),
        convertBufferPathToString('response.attestationObject'),
        convertBufferPathToString('response.clientDataJSON'),
    ])(credential);
};

const useWebAuthnSettingsData = () => {
    const { t } = useTranslation(['userSelf', 'common']);
    const apolloClient = useApolloClient();
    const account = useAccount(true);

    const userInfo = useMemo(
        () =>
            account
                ? {
                      id: base64ToArrayBuffer(btoa(account.id)),
                      name: account.email,
                      displayName: account.displayName,
                  }
                : null,
        [account]
    );

    const { data, refetch, loading } = api.useGetAuthenticatedUserWebAuthnKeysQuery({
        fetchPolicy: 'cache-and-network',
    });

    const revokeKey = useCallback(
        (keyId: string) => {
            Modal.confirm({
                title: t('userSelf:webAuthnSettings.revokeModal.title'),
                icon: <ExclamationCircleOutlined />,
                content: t('userSelf:webAuthnSettings.revokeModal.content'),
                okText: t('userSelf:webAuthnSettings.revokeModal.okText'),
                okType: 'danger',
                cancelText: t('userSelf:webAuthnSettings.revokeModal.cancelText'),
                onOk: async () => {
                    try {
                        const results = await apolloClient.mutate<
                            api.RevokeWebPublicKeyCredentialMutation,
                            api.RevokeWebPublicKeyCredentialMutationVariables
                        >({
                            mutation: api.RevokeWebPublicKeyCredentialDocument,
                            variables: { keyId },
                        });

                        const rawKeyId = results.data?.revokeWebPublicKeyCredential;

                        if (rawKeyId) {
                            // check the persisted key ID on local
                            const persistedKeyId = localStorage.getItem('app:webauthnKeyId');

                            if (persistedKeyId === rawKeyId) {
                                // unset it
                                localStorage.removeItem('app:webauthnKeyId');
                            }

                            // also refetch keys
                            refetch();

                            // notify the user
                            // display a notification
                            notification.success({
                                message: t('userSelf:webAuthnSettings.deleteMessage.title'),
                                description: t('userSelf:webAuthnSettings.deleteMessage.description'),
                            });
                        }
                    } catch (error) {
                        if (error instanceof ApolloError) {
                            message.error(error.graphQLErrors[0].message);
                        }
                    }
                },
            });
        },
        [apolloClient, refetch, t]
    );

    const setupNewKey = useCallback(async () => {
        const { data: request } = await apolloClient.mutate<
            api.RequestWebPublicKeyCredentialRegistrationMutation,
            api.RequestWebPublicKeyCredentialRegistrationMutationVariables
        >({ mutation: api.RequestWebPublicKeyCredentialRegistrationDocument });

        const { options, token } = request.requestWebPublicKeyCredentialRegistration;
        const attestation = {
            ...options,
            user: userInfo,
            challenge: base64ToArrayBuffer(options.challenge),
        };

        const credential = await navigator.credentials.create({ publicKey: attestation });
        const response = convertResponse(credential);

        const { data } = await apolloClient.mutate<
            api.CompleteWebPublicKeyCredentialRegistrationMutation,
            api.CompleteWebPublicKeyCredentialRegistrationMutationVariables
        >({
            mutation: api.CompleteWebPublicKeyCredentialRegistrationDocument,
            variables: { token, response },
        });

        if (data?.completeWebPublicKeyCredentialRegistration) {
            // set the key as active in local storage
            localStorage.setItem('app:webauthnKeyId', response.rawId);

            // refetch keys
            refetch();

            // display a notification
            notification.success({
                message: t('userSelf:webAuthnSettings.successMessage.title'),
                description: t('userSelf:webAuthnSettings.successMessage.description'),
            });
        }
    }, [apolloClient, userInfo, refetch, t]);

    const keys = useMemo(
        () =>
            (data?.currentUser?.webAuthnKeys || [])
                .map(key => ({
                    ...key,
                    expiresAt: new Date(key.expiresAt),
                    lastUsed: key.lastUsed ? new Date(key.lastUsed) : null,
                }))
                .sort((a, b) => {
                    const diffOnLastUsed = (b.lastUsed?.getTime() || Infinity) - (a.lastUsed?.getTime() || Infinity);

                    if (diffOnLastUsed === 0 || Number.isNaN(diffOnLastUsed)) {
                        return b.expiresAt.getTime() - a.expiresAt.getTime();
                    }

                    return diffOnLastUsed;
                }),
        [data]
    );

    const keysData = useMemo(
        () =>
            keys.map(key => {
                const items: SimpleListItemProps[] = [
                    {
                        key: 'userAgent',
                        label: t('userSelf:webAuthnSettings.table.titles.userAgent'),
                        title: key.userAgent,
                        type: 'large',
                    },
                    {
                        key: 'expiresAt',
                        label: t('userSelf:webAuthnSettings.table.titles.expiresAt'),
                        title: t('common:formats.dateTime', { date: key.expiresAt }),
                    },
                    {
                        key: 'lastUsed',
                        label: t('userSelf:webAuthnSettings.table.titles.lastUsed'),
                        title: key.lastUsed ? t('common:formats.dateTime', { date: key.lastUsed }) : '-',
                    },
                ];

                return {
                    id: key.id,
                    items,
                };
            }),
        [keys, t]
    );

    return {
        keysData,
        revokeKey,
        setupNewKey,
        loading,
    };
};

export default useWebAuthnSettingsData;
